import LegalLayout, { H2, H3, P, UL, LI, Verbatim } from "@/pages/LegalLayout";
import { BUSINESS } from "@/lib/site";

export default function Privacy() {
  return (
    <LegalLayout
      testid="privacy-page"
      title="Privacy Policy"
      updated="June 2026"
    >
      <P>
        This Privacy Policy explains how Rolling Stone Marketing ("Rolling Stone
        Marketing," "we," "us," or "our") collects, uses, and protects
        information when you visit {BUSINESS.domain} (the "Site") or contact us
        through the form on this Site. By using the Site, you agree to the
        practices described here.
      </P>

      <H2 id="information-collected">1. Information We Collect</H2>
      <P>We collect the following categories of information:</P>
      <UL>
        <LI>
          <strong>Identifiers.</strong> First name, last name, company name,
          email address, and mobile phone number that you provide through the
          contact form.
        </LI>
        <LI>
          <strong>Commercial information.</strong> The service you indicate you
          are interested in and the content of any message you send us.
        </LI>
        <LI>
          <strong>Consent records.</strong> The state of each optional consent
          checkbox (SMS and email), the exact consent language you agreed to,
          the date and time of your submission (UTC timestamp), and the IP
          address from which the submission was made. These records exist as
          proof of opt-in.
        </LI>
        <LI>
          <strong>Technical information.</strong> Standard information such as
          browser type and IP address that is transmitted automatically when
          you interact with the Site.
        </LI>
      </UL>

      <H2 id="how-used">2. How We Use Information</H2>
      <P>We use the information we collect to:</P>
      <UL>
        <LI>Respond to your inquiry and communicate with you about our services;</LI>
        <LI>
          Send you text messages and/or emails only if you have separately
          opted in to each channel;
        </LI>
        <LI>Maintain records of consent as required by law;</LI>
        <LI>Operate, secure, and improve the Site.</LI>
      </UL>

      <H2 id="sms">3. SMS / Text Messaging</H2>
      <P>
        Consent to receive text messages from Rolling Stone Marketing is
        collected only via the optional SMS checkbox on the contact form on this
        website. We never obtain SMS consent from purchased, rented, appended,
        or third-party lists.
      </P>
      <H3>Program description</H3>
      <P>
        If you opt in, Rolling Stone Marketing sends recurring marketing and
        customer care text messages related to your inquiry and our services.
      </P>
      <H3>Message frequency</H3>
      <P>Message frequency varies (approximately 2-6 messages per month).</P>
      <H3>Cost</H3>
      <P>Message and data rates may apply.</P>
      <H3>Opt out and help</H3>
      <P>
        Reply STOP at any time to opt out. Reply HELP for help. You may also
        contact us at {BUSINESS.email} or {BUSINESS.phone}.
      </P>
      <Verbatim testid="privacy-verbatim-mobile">
        No mobile information will be shared with third parties or affiliates
        for marketing or promotional purposes. All of the above categories
        exclude text messaging originator opt-in data and consent; this
        information will not be shared with any third parties.
      </Verbatim>

      <H2 id="sharing">4. How We Share Information</H2>
      <P>
        We share information only in the limited circumstances below, and never
        for third-party marketing:
      </P>
      <UL>
        <LI>
          <strong>Service providers under contract.</strong> Vendors that help
          us operate the Site and communicate with you (for example, hosting,
          email, and messaging providers), who are contractually bound to use
          the information only to provide services to us.
        </LI>
        <LI>
          <strong>Legal requirements.</strong> When required to comply with
          applicable law, regulation, legal process, or an enforceable
          governmental request.
        </LI>
        <LI>
          <strong>Business transfer.</strong> In connection with a merger,
          acquisition, or sale of assets, subject to the protections of this
          Policy.
        </LI>
      </UL>
      <P>We do not share information with any other parties.</P>
      <Verbatim testid="privacy-verbatim-nosale">
        Information collected through this Site will not be sold, rented,
        leased, or otherwise shared with third parties for promotional or
        marketing purposes.
      </Verbatim>

      <H2 id="data-services-separate">
        5. Our Data Services and Your Information Are Separate
      </H2>
      <P>
        Rolling Stone Marketing offers commercial data products — including aged
        data, web scraping, and data appending. Those products operate on
        separately sourced commercial data that is governed by client
        agreements and is entirely distinct from this website.
      </P>
      <P>
        Information you submit through this website — including your name, email
        address, mobile phone number, message content, and your SMS or email
        consent — is never included in, compiled into, appended to, or offered
        as part of any data product, and is never made available to any client
        or third party for marketing purposes. These are separate systems, and
        site visitor data does not flow into our data products.
      </P>

      <H2 id="retention">6. Data Retention</H2>
      <P>
        We retain contact submissions and consent records for as long as
        necessary to respond to your inquiry, to maintain proof of consent, and
        to comply with our legal obligations. Consent records may be retained
        for the period required to demonstrate compliance and may be produced
        during a carrier or regulatory audit.
      </P>

      <H2 id="security">7. Security</H2>
      <P>
        We use reasonable administrative, technical, and physical safeguards
        designed to protect the information we hold. No method of transmission
        or storage is completely secure, and we cannot guarantee absolute
        security.
      </P>

      <H2 id="rights">8. Your Rights</H2>
      <P>Depending on your location, you may have the right to:</P>
      <UL>
        <LI>Access the personal information we hold about you;</LI>
        <LI>Request correction of inaccurate information;</LI>
        <LI>Request deletion of your information;</LI>
        <LI>
          Not be discriminated against for exercising any of these rights.
        </LI>
      </UL>
      <P>
        To exercise any of these rights, contact us at {BUSINESS.email}.
      </P>

      <H2 id="cookies">9. Cookies</H2>
      <P>
        The Site may use a minimal set of cookies and similar technologies
        necessary for the Site to function and to understand basic usage. You
        can control cookies through your browser settings.
      </P>

      <H2 id="links">10. Third-Party Links</H2>
      <P>
        The Site may contain links to third-party websites. We are not
        responsible for the privacy practices of those sites, and we encourage
        you to review their policies.
      </P>

      <H2 id="changes">11. Changes to This Policy</H2>
      <P>
        We may update this Privacy Policy from time to time. Changes are
        effective when posted to this page, and the "Last updated" date above
        reflects the most recent revision.
      </P>

      <H2 id="contact">12. Contact Us</H2>
      <P>
        Rolling Stone Marketing
        <br />
        {BUSINESS.physicalAddress}
        <br />
        Mailing: {BUSINESS.mailingAddress}
        <br />
        Email: {BUSINESS.email}
        <br />
        Phone: {BUSINESS.phone}
        <br />
        Hours: {BUSINESS.hours}
      </P>
    </LegalLayout>
  );
}
